const React = window.React;
const ReactDOM = window.ReactDOM;
const createRoot = ReactDOM?.client?.createRoot;
if( !createRoot ) {
    console.warn( 'Failed to load sidebar script' );
}

const HIDDEN_WIDGET_CLASS = 'adw-sidebar-widget-hidden';
const COLLAPSE_ENABLED_PROPERTY = '--adw-article-sidebar-collapse-enabled';
const RESIZE_DEBOUNCE_MS = 100;

function initializeSidebarHeightAdjuster() {
    function SidebarShowMore( { onExpand } ) {
        return (
            <div
                className="position-absolute bottom-0 w-100 d-flex justify-content-center align-items-end align-content-end flex-wrap"
                style={{
                    background: 'linear-gradient(to top, var(--bs-body-bg), var(--bs-body-bg) 10%, transparent 100%)',
                    height: '200px',
                }}
            >
                <button
                    type="button"
                    onClick={onExpand}
                    className="btn-sidebar-show-more btn rounded-pill btn-outline-primary align-self-center"
                >
                    Show More
                </button>
            </div>
        );
    }

    function getSidebarWidgets( elSidebar ) {
        return Array.from( elSidebar.children ).filter( elChild => (
            elChild.classList.contains( 'widget-wrapper' )
        ) );
    }

    function getStickyAdWidget( widgets ) {
        const lastWidget = widgets[ widgets.length - 1 ];

        return lastWidget && (
            lastWidget.classList.contains( 'adw-sticky-widget' )
            || lastWidget.classList.contains( 'widget-adweek-sticky-next' )
        )
            ? lastWidget
            : null;
    }

    const isSinglePost = document.querySelector( 'body.single-post' );
    if ( !isSinglePost || !createRoot ) return;

    const articleBody = document.querySelector( '#article .layout-left-col' );
    if ( !articleBody ) return;

    const articleSidebar = document.querySelector( '#article .sidebar' );
    if ( !articleSidebar ) return;

    const widgets = getSidebarWidgets( articleSidebar );
    if ( !widgets.length ) return;

    const stickyAdWidget = getStickyAdWidget( widgets );
    const widgetHeights = new WeakMap();
    let isExpanded = false;

    articleSidebar.classList.add( 'position-relative' );

    const showMoreHost = document.createElement( 'div' );
    showMoreHost.classList.add(
        'adw-sidebar-show-more',
        'position-relative',
        'w-100',
        'z-1',
        'd-none'
    );

    if ( stickyAdWidget ) {
        stickyAdWidget.insertAdjacentElement( 'beforebegin', showMoreHost );
    } else {
        articleSidebar.insertAdjacentElement( 'beforeend', showMoreHost );
    }

    const showMoreRoot = createRoot( showMoreHost );

    function cacheVisibleWidgetHeights() {
        widgets.forEach( elWidget => {
            if ( !elWidget.classList.contains( HIDDEN_WIDGET_CLASS ) ) {
                widgetHeights.set( elWidget, elWidget.offsetHeight );
            }
        } );
    }

    function getRowGap() {
        const rowGap = window.getComputedStyle( articleSidebar ).rowGap;
        const parsedRowGap = Number.parseFloat( rowGap );

        return Number.isNaN( parsedRowGap ) ? 0 : parsedRowGap;
    }

    function getHiddenWidgets( articleBodyHeight ) {
        const rowGap = getRowGap();
        const nonStickyWidgets = widgets.filter( widget => widget !== stickyAdWidget );
        const visibleWidgets = new Set( widgets );
        let visibleWidgetsHeight = widgets.reduce( (
            totalHeight,
            widget
        ) => totalHeight + ( widgetHeights.get( widget ) || 0 ), 0 );

        const expandedSidebarHeight = visibleWidgetsHeight
            + ( rowGap * Math.max( widgets.length - 1, 0 ) );

        if ( expandedSidebarHeight <= articleBodyHeight ) {
            return [];
        }

        const hideableWidgets = nonStickyWidgets.slice( 1 ).reverse();

        for ( const widget of hideableWidgets ) {
            const collapsedSidebarHeight = visibleWidgetsHeight
                + ( rowGap * visibleWidgets.size );

            if ( collapsedSidebarHeight <= articleBodyHeight ) break;

            visibleWidgets.delete( widget );
            visibleWidgetsHeight -= widgetHeights.get( widget ) || 0;
        }

        return widgets.filter( widget => !visibleWidgets.has( widget ) );
    }

    function applyHiddenWidgets( hiddenWidgets ) {
        const hiddenWidgetSet = new Set( hiddenWidgets );

        widgets.forEach( widget => {
            widget.classList.toggle(
                HIDDEN_WIDGET_CLASS,
                hiddenWidgetSet.has( widget )
            );
        } );
    }

    function renderShowMore( shouldRender ) {
        showMoreHost.classList.toggle( 'd-none', !shouldRender );
        showMoreRoot.render(
            shouldRender
                ? <SidebarShowMore onExpand={expandArticleSidebar} />
                : null
        );
    }

    function resetArticleSidebar() {
        applyHiddenWidgets( [] );
        renderShowMore( false );
    }

    function expandArticleSidebar() {
        isExpanded = true;
        resetArticleSidebar();
    }

    function isCollapseEnabled() {
        return window.getComputedStyle( articleSidebar ).getPropertyValue(
            COLLAPSE_ENABLED_PROPERTY
        ).trim() === '1';
    }

    function syncArticleSidebar() {
        cacheVisibleWidgetHeights();

        if ( !isCollapseEnabled() || isExpanded ) {
            resetArticleSidebar();
            return;
        }

        const hiddenWidgets = getHiddenWidgets( articleBody.offsetHeight );
        applyHiddenWidgets( hiddenWidgets );
        renderShowMore( hiddenWidgets.length > 0 );
    }

    const scheduleArticleSidebarSync = window.debounce(
        syncArticleSidebar,
        RESIZE_DEBOUNCE_MS
    );
    const resizeObserver = new ResizeObserver( scheduleArticleSidebarSync );
    resizeObserver.observe( articleSidebar );
    resizeObserver.observe( articleBody );

    syncArticleSidebar();
}

window.addEventListener('load', initializeSidebarHeightAdjuster, {once: true});
